/**
 * ONE ENGAGEMENT — a place, not a row in somebody else's list (R5.1).
 *
 * An engagement is the professional context a piece of work belongs to: the
 * client, the period, and the cut-off every figure is established as at. Until
 * now it existed only as a line inside a client screen, with no address of its
 * own — so an accountant could open a client and open a piece of work, but
 * never open the engagement the work was FOR.
 *
 * WHAT IT IS FOR, and what it deliberately is not. It states the context and
 * lists the work opened against it. It computes no state: every label and tone
 * on a work row is the board's own projection, read rather than re-decided,
 * and the cut-off is the engagement's own — the one date the capability
 * denominates every figure by, and the one an out-of-period record is measured
 * against.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EngagementContextResponse, WorkBoardResponse, type Client, type Engagement, type WorkSummary } from "@leveri/contracts";
import { Citation, Definitions, FileTitle, Notice, Reading, RuledList, RuledRow, Section, SectionHead, StateText, markerTone } from "../file/index.js";
import { call, failureMessage } from "../api.js";
import { Desk, usePlaceWords } from "./shell.js";
import { MaterialitySection } from "./materiality.js";
import { EngagementFileSection } from "./engagementFile.js";
import { AssignControl, type Holder } from "./assign.js";
import { ChatBox } from "./chat.js";

export function EngagementScreen() {
  const { engagementId } = useParams<{ engagementId: string }>();
  const navigate = useNavigate();
  const place = usePlaceWords();
  const [engagement, setEngagement] = useState<Engagement | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [holder, setHolder] = useState<Holder | null>(null);
  const [work, setWork] = useState<WorkSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!engagementId) return;
    setFailure(null);
    try {
      const [context, board] = await Promise.all([
        call("GET", `/api/engagements/${engagementId}`, EngagementContextResponse),
        call("GET", "/api/work", WorkBoardResponse),
      ]);
      setEngagement(context.engagement);
      setClient(context.client);
      setHolder(context.assignee);
      // A SELECTION, NOT A JUDGMENT. No status, label, tone or grouping is
      // recomputed here; the rows are the board's own, narrowed to this
      // engagement. (The same residual the client screen carries: it reads the
      // whole board to show one engagement's part.)
      setWork(board.groups.flatMap((group) => group.work).filter((row) => row.engagementId === engagementId));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [engagementId]);

  useEffect(() => { void load(); }, [load]);

  if (failure) {
    return (
      <Desk current="clients">
        <Notice>{failure}</Notice>
      </Desk>
    );
  }
  if (!engagement || !client) {
    return <Desk current="clients"><Reading>Opening the {place.engagement}…</Reading></Desk>;
  }

  return (
    <Desk current="clients">
      <FileTitle kicker={client.name} meta={engagement.periodText}>{engagement.title}</FileTitle>

      {/* The chat box, scoped to this engagement: never asks who or when. */}
      <div className="mt-6"><ChatBox scope={{ engagementId: engagement.id }} placeholder={`What should Leveri do for ${engagement.title}?`} /></div>

      <Section>
        <SectionHead>The {place.engagement}</SectionHead>
        {/* THE PERIOD IS NOT DECORATION. Every figure the capability
            establishes is established as at the cut-off, and a record that
            runs past it is the one thing that stops a reconciliation before it
            starts — which is why the date is stated here, in full, rather than
            being something a person has to open a form to see. */}
        <Definitions
          items={[
            { key: "client", term: "Client", value: client.name },
            { key: "period", term: "Period", value: engagement.periodText },
            { key: "currency", term: "Currency", value: engagement.currency },
            { key: "cutoff", term: "Cut-off", value: `Every figure is established as at the end of ${engagement.periodText.split(" to ")[1] ?? engagement.periodText}` },
            { key: "holder", term: "Held by", value: <AssignControl objectKind="ENGAGEMENT" objectId={engagement.id} holder={holder} onChanged={setHolder} /> },
          ]}
        />
        <div className="mt-3">
          <Citation>{engagement.periodStart} … {engagement.cutOff}</Citation>
        </div>
      </Section>

      <EngagementFileSection engagement={engagement} />

      <MaterialitySection engagement={engagement} />

      <Section>
        <SectionHead aside={work && work.length > 0 ? String(work.length) : null}>Work in this {place.engagement}</SectionHead>
        {!work || work.length === 0 ? (
          <p className="text-sm text-stone">
            No work has been opened against this engagement yet. Work is opened from the client.
          </p>
        ) : (
          <RuledList>
            {work.map((row) => (
              <RuledRow as="button" key={row.id} className="px-3" onClick={() => navigate(`/work/${row.id}`)}>
                <span className="min-w-0">
                  <span className="block truncate text-[15px] text-ink">{row.title}</span>
                  <Citation className="mt-0.5 block">{row.periodText}</Citation>
                </span>
                <StateText tone={markerTone(row.tone)}>{row.statusLabel}</StateText>
              </RuledRow>
            ))}
          </RuledList>
        )}
      </Section>
    </Desk>
  );
}
